import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../api/auth'

// Live server push over SSE. The API mints a short-lived stream token
// (EventSource can't send an Authorization header), then we connect to
// /api/stream and receive org-scoped `fleet` payloads and `alert` events.
//
// Reconnect strategy: the stream token expires in ~60s, so EventSource's
// native auto-reconnect would 401. We disable it by closing on error and
// reconnecting ourselves with a fresh token + exponential backoff (1s → 30s).
// The caller keeps its polling fallback active whenever `connected` is false,
// so a broken stream degrades to exactly today's behaviour.
const BASE = '/api'

export function useLiveStream({ onFleet, onAlert, onAlertUpdate, enabled = true }) {
  const [connected, setConnected] = useState(false)
  // Handlers live in a ref so a re-render never tears down the connection.
  const handlers = useRef({ onFleet, onAlert, onAlertUpdate })
  handlers.current = { onFleet, onAlert, onAlertUpdate }

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return undefined
    let es = null
    let closed = false
    let retryMs = 1000
    let retryTimer = null

    const scheduleRetry = () => {
      if (closed) return
      retryTimer = setTimeout(connect, retryMs)
      retryMs = Math.min(retryMs * 2, 30000)
    }

    async function connect() {
      if (closed) return
      let token = null
      try {
        const data = await apiFetch('/stream/token', { method: 'POST' })
        token = data?.token
      } catch {
        /* API unreachable — fall through to retry, polling covers the gap */
      }
      if (!token) return scheduleRetry()
      es = new EventSource(`${BASE}/stream?token=${encodeURIComponent(token)}`)
      // `hello` is the server's first frame — only then are we truly live.
      es.addEventListener('hello', () => { setConnected(true); retryMs = 1000 })
      es.addEventListener('fleet', (e) => {
        try { handlers.current.onFleet?.(JSON.parse(e.data)) } catch { /* skip bad frame */ }
      })
      es.addEventListener('alert', (e) => {
        try { handlers.current.onAlert?.(JSON.parse(e.data)) } catch { /* skip bad frame */ }
      })
      // Workflow state change on an existing alert (ack/resolve/assign/reopen).
      es.addEventListener('alert_update', (e) => {
        try { handlers.current.onAlertUpdate?.(JSON.parse(e.data)) } catch { /* skip bad frame */ }
      })
      es.onerror = () => {
        setConnected(false)
        es?.close()
        scheduleRetry()
      }
    }

    connect()
    return () => {
      closed = true
      clearTimeout(retryTimer)
      es?.close()
      setConnected(false)
    }
  }, [enabled])

  return { connected }
}
